import React from "react";
import { CheckCircle2, Zap, TrendingUp, Lightbulb } from "lucide-react";

const ZeroToHeroInfographic = () => {
  const scrollToContact = () => {
    const element = document.getElementById("contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const phases = [
    {
      name: "IDEATE",
      icon: Lightbulb,
      steps: [1],
    },
    {
      name: "VALIDATE",
      icon: CheckCircle2,
      steps: [2, 3, 4],
    },
    {
      name: "AUTOMATE",
      icon: Zap,
      steps: [5],
    },
    {
      name: "OPTIMISE",
      icon: TrendingUp,
      steps: [6],
    },
  ];

  const steps = [
    {
      id: 1,
      title: "Ideation & opportunity discovery",
      description:
        "Explore problems, brainstorm AI opportunities, and identify high-impact use cases aligned with business goals.",
      phase: "IDEATE",
    },
    {
      id: 2,
      title: "AI discovery session",
      description:
        "Identify opportunities to transform your business with AI, calculate ROI, prioritise use cases.",
      phase: "VALIDATE",
    },
    {
      id: 3,
      title: "AI scoping programme",
      description:
        "Build a robust AI strategy, predict and mitigate all possible risk factors, build a detailed roadmap.",
      phase: "VALIDATE",
    },
    {
      id: 4,
      title: "Proof of concept (PoC)",
      description:
        "Validate that a solution will work by building the simplest possible version of your product.",
      phase: "VALIDATE",
    },
    {
      id: 5,
      title: "Minimum viable product (MVP)",
      description:
        "Turn the PoC into the simplest version of a usable product that can be beta tested by external users.",
      phase: "AUTOMATE",
    },
    {
      id: 6,
      title: "Ongoing maintenance",
      description:
        "Fully customised managed services agreement to maintain and enhance your product and help you keep up with the latest technology trends.",
      phase: "OPTIMISE",
    },
  ];

  return (
    <section id="zero-to-hero" className="section-padding bg-gray-50 py-20">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold mb-4">
            From Zero to Hero
          </h2>
          <p className="text-xl text-gray-600">
            Our team of experts will guide you from AI opportunities to
            implementation.
          </p>
        </div>

        {/* Process Flow Diagram - Matching Image Exactly */}
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left Side - Main Phases */}
            <div className="space-y-12 lg:space-y-24 relative">
              {phases.map((phase, index) => {
                const IconComponent = phase.icon;
                const phaseSteps = steps.filter((s) => s.phase === phase.name);
                const stepCount = phaseSteps.length;

                return (
                  <div
                    key={phase.name}
                    className="relative"
                    style={{
                      minHeight:
                        stepCount === 3
                          ? "280px"
                          : stepCount === 1
                          ? "140px"
                          : "140px",
                    }}
                  >
                    {/* Phase Icon - Square with rounded corners, gradient */}
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="w-20 h-20 bg-gradient-to-br from-[#2aa996] to-[#2aa996] rounded-xl flex items-center justify-center shadow-xl flex-shrink-0">
                        <IconComponent className="text-white" size={36} />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-800 uppercase tracking-wider">
                          {phase.name}
                        </h3>
                      </div>
                    </div>

                    {/* Connecting Dotted Line (except for last) - centered on icon */}
                    {index < phases.length - 1 && (
                      <div
                        className="absolute left-10 top-24 w-0.5 border-l-2 border-dashed border-gray-400"
                        style={{
                          height: `${phaseSteps.length * 120}px`,
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Right Side - Steps */}
            <div className="space-y-6 pt-2">
              {steps.map((step) => {
                return (
                  <div
                    key={step.id}
                    className="bg-white rounded-xl p-6 shadow-md border-l-4 border-[#2aa996] hover:shadow-lg transition-all duration-300"
                  >
                    <div className="flex items-start space-x-4">
                      {/* Step Number - Purple Circle */}
                      <div className="w-12 h-12 bg-[#2aa996] rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                        <span className="text-white font-bold text-lg">
                          {step.id}
                        </span>
                      </div>

                      {/* Step Content */}
                      <div className="flex-1">
                        <h4 className="text-xl font-bold text-gray-800 mb-2">
                          {step.title}
                        </h4>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="text-center mt-16">
          <button
            onClick={scrollToContact}
            className="bg-[#2aa996] hover:bg-[#1e7a6b] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors duration-300 shadow-lg"
          >
            Start Your AI Journey
          </button>
        </div>
      </div>
    </section>
  );
};

export default ZeroToHeroInfographic;
